import { format } from 'date-fns';
import { WC_CART_STORE_NAME } from '../constants';

/**
 * Get current selected shipping method for a package.
 * @param int package_id Package ID.
 * @returns 
 */
export function getSelectedShippingMethod(package_id = 0) {
	if (isBlockCheckout()) {
		let shippingRates = wp.data.select('wc/store/cart').getShippingRates();
		
		if (!shippingRates || !shippingRates[package_id]) {
			return false;
		}

		let selectedShipping = shippingRates[package_id].shipping_rates.find((rate) => rate.selected === true);
		return selectedShipping?.rate_id ? selectedShipping.rate_id : false;
	}

	// Classic checkout - get value from shipping method input
	let shippingMethodInput = document.querySelector(`input[name="shipping_method[${package_id}]"]:checked, input[name="shipping_method[${package_id}]"][type="hidden"]`);

	return shippingMethodInput ? shippingMethodInput.value : false;
}

/**
 * Get selected shipping method for a subscription.
 * @param {Object} cartData   Cart data.
 * @param {number} package_id Package ID.
 * @returns 
 */
export function getSelectedShippingMethodForSubscription(cartData = false, package_id = 0) {
	if (isBlockCheckout()) {
		if (!cartData) {
			cartData = wp.data.select('wc/store/cart').getCartData();
		}

		const subscription = cartData?.extensions?.subscriptions?.[package_id];
		if (!subscription) {
			// Fallback to regular shipping method if subscription shipping method is not available.
			return getSelectedShippingMethod(package_id);
		}

		const shippingMethods = subscription?.shipping_rates?.[package_id]?.shipping_rates;
		if ( ! shippingMethods ) {
			return false;
		}

		const selectedShippingMethod = shippingMethods.find(rate => rate.selected);
		return selectedShippingMethod.rate_id;
	}

	return getSelectedShippingMethod(package_id);
}

/**
 * Get labels.
 * 
 * @param string key Label Id/key.
 *
 * @returns string
 */
export function getLabel( key ) {
	if ( ! wcSettings[ 'iconic-wds_data' ] || ! wcSettings[ 'iconic-wds_data' ]?.labels ) {
		return '';
	}

	return wcSettings[ 'iconic-wds_data' ].labels[ key ];
}

/**
 * Get any setting.
 * 
 * @param string WPSF Setting key/ID.
 * @returns 
 */
export function getSetting( key = '' ) {
	const settings = getDataFromGlobalVars('settings');
	if (!settings) { 
		return '';
	}

	if ( key ) {
		return settings[ key ];
	}
	
	return settings;
}

/**
 * When timeslot is changed then the fees should be updated.
 * This function updates the cart which forces the fees to be recalculated.
 *
 * @returns 
 */
export function updateCart() {
	const { extensionCartUpdate } = wc.blocksCheckout;

	let extensionData = wp.data.select( 'wc/store/checkout' ).getExtensionData();

	if ( ! extensionData || ! extensionData[ 'iconic-wds' ] ) {
		return;
	}

	extensionCartUpdate( {
		namespace: 'iconic-wds',
		data: {
			date: extensionData[ 'iconic-wds' ].date,
			date_ymd: extensionData[ 'iconic-wds' ].date_ymd,
			timeslot: extensionData[ 'iconic-wds' ].timeslot,
			shipping_method: getSelectedShippingMethod(0),
		},
	} );
}

/**
 * Is timeslot enabled?
 * @returns bool
 */
export function isTimeslotEnabled() {
	const settings = getDataFromGlobalVars('settings');
	return isTrue(settings.timesettings_timesettings_setup_enable);
}

/**
 * Is true?
 * @param mixed value
 * @returns bool
 */
export function isTrue(value) {
	return 1 === value || '1' === value;
}

/**
 * Is timeslot mandatory?
 * @returns bool
 */
export function isTimeslotMandatory() {
	const settings = getDataFromGlobalVars('settings');
	return isTrue(settings.timesettings_timesettings_setup_mandatory);
}

/**
 * Is auto-select first date setting enabled?
 * @returns bool
 */
export function autoSelectFirstDate() {
	const settings = getDataFromGlobalVars('settings');
	return isTrue(settings.datesettings_datesettings_setup_auto_select_first);
}

/**
 * Is auto-select first date setting enabled?
 * @returns bool
 */
export function autoSelectTimeslot() {
	const settings = getDataFromGlobalVars('settings');
	return isTrue(settings.timesettings_timesettings_setup_auto_select_first);
}

/**
 * Get the start day of the week.
 * @returns int
 */
export function getCalendarStartDay() {
	const settings = getDataFromGlobalVars('settings');
	let week_last_day = settings.datesettings_datesettings_last_day_of_week;

	let map = {
		'sunday': 1,
		'monday': 2,
		'tuesday': 3,
		'wednesday': 4,
		'thursday': 5,
		'friday': 6,
		'saturday': 0,
	};

	return map[ week_last_day ];
}

/**
 * Calculate and return the fee for the given date.
 * @param Date date
 * @returns 
 */
export function getFeeForDate ( date ) {
	let day_number = date.getDay();
	let ymd = format( date, 'yyyyMMdd' );
	let fee = typeof jckwds_vars.day_fees[ ymd ] !== 'undefined'
			? parseFloat( jckwds_vars.day_fees[ ymd ] )
		: parseFloat( jckwds_vars.day_fees[ day_number ] );
	
	let same_day_fees = getSetting( 'datesettings_fees_same_day' );
	let next_day_fees = getSetting( 'datesettings_fees_next_day' );
	
	// Same day
	if (
		ymd === jckwds_vars.dates.same_day &&
		same_day_fees > 0
	) {
		fee += parseFloat(
			same_day_fees
		);
	}

	// Next day
	if (
		ymd === jckwds_vars.dates.next_day &&
		next_day_fees > 0
	) {
		fee += parseFloat(
			next_day_fees
		);
	}

	return isNaN( fee ) ? false : accounting.formatMoney( fee, jckwds_vars.currency );
}

export function isBlockCheckout() {
	return jQuery("[data-block-name='woocommerce/checkout']").length > 0 || wc.blocksCheckout;
}

/**
 * Get the global object which contains the global variables i.e jckwds_vars, wcSettings, etc.
 * @returns {Object}
 */
export function getGlobalObject() {
	if (typeof wcSettings !== 'undefined') {
		if (wcSettings['iconic-wds_data'] && Object.keys(wcSettings['iconic-wds_data']).length > 0) {
			return wcSettings['iconic-wds_data'];
		}
		if (wcSettings['iconic-wds-subscriptions_data'] && Object.keys(wcSettings['iconic-wds-subscriptions_data']).length > 0) {
			return wcSettings['iconic-wds-subscriptions_data'];
		}
	}

	if (typeof window !== 'undefined' && window.jckwds_vars && Object.keys(window.jckwds_vars).length > 0) {
		return window.jckwds_vars;
	}

	return {};
}

export function getDataFromGlobalVars( key = false ) {
	// Ideally all the keys in the block checkout and classic checkout should be same.
	// But they are not at this point.
	const isBlockCheckoutVar = isBlockCheckout();
	const globalVars = getGlobalObject();

	if (!globalVars) {
		return false;
	}

	if (!isBlockCheckoutVar) {
		if ( 'i18n' === key ) {
			return prepareFnsLocaleFromJckwdsVars(globalVars);
		}
	}
	
	if (key) {
		return globalVars[ key ];
	}

	return globalVars;
}

function prepareFnsLocaleFromJckwdsVars(globalVars) {
	const strings = globalVars.strings;

	const locale = {
		days: strings.days,
		days_short: strings.days_short,
		months: strings.months,
		months_short: strings.months_short,
	};

	return locale;
}

/**
 * Convert date from yyyyMMdd format to Date object
 * 
 * @param {string} dateYmd Date in yyyyMMdd format
 * @returns {Date|null} Date object or null if invalid date
 */
export function dateYmdToDate(dateYmd) {
    if (!dateYmd) {
        return null;
    }

    // Convert from yyyyMMdd to yyyy-MM-dd
    const year = dateYmd.substring(0, 4);
    const month = dateYmd.substring(4, 6);
    const day = dateYmd.substring(6, 8);
    const formattedDate = `${year}-${month}-${day}`;
    
    // Create and validate Date object
    const dateObj = new Date(formattedDate);
    return isNaN(dateObj.getTime()) ? null : dateObj;
}

/**
 * Convert date from yyyyMMdd format to formatted date string
 * 
 * @param {string} dateYmd Date in yyyyMMdd format
 * @returns {string|null} Formatted date string or null if invalid date
 */
export function dateYmdToFormattedDate(dateYmd) {
	if (!dateYmd) {
		return null;
	}

	let dateFormat;
	if (isBlockCheckout()) {
		const globalVars = getGlobalObject();
		dateFormat = globalVars.date_fns_format;
	} else {
		dateFormat = jckwds_vars.date_fns_format;
	}

	return format(dateYmdToDate(dateYmd), dateFormat);
}

/**
 * Get shipping method label
 * 
 * @param {string} shippingMethodId
 * @returns {string}
 */
export const getShippingMethodLabelType = ( shippingMethodId ) => {
	const defaultLabel = getSetting('general_setup_labels');
	const shippingMethodLabels = getDataFromGlobalVars('shipping_method_labels');

	if (!shippingMethodLabels) {
		return defaultLabel;
	}

	if (!shippingMethodLabels[shippingMethodId] || 'default' === shippingMethodLabels[shippingMethodId]) {
		return defaultLabel;
	}

	return shippingMethodLabels[shippingMethodId];
}